import { c as _c } from "react/compiler-runtime";
import { ChevronDown, PenSquare, CircleUserRound } from "lucide-react";
import { Button } from "../Button";
import { Dropdown } from "../Dropdown";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { useViewer } from "common/hooks/data/useViewer";
import { usePageProps } from "common/hooks/data/usePageProps";
import { useRouter } from "common/lib/router";
import type { AppProps } from "common/lib/getAppServerSideProps";
const ANALYTICS_URL = "https://analytics.theconversation.com?utm_source=theconversation.com&utm_medium=website&utm_campaign=account_menu";

/**
 * Component responsible to render account menu actions, such as Button to the viewer profile, sign out Button, etc.
 * In case the viewer is not logged in, it will render a "sign in" button instead.
 */
export const AccountMenu = () => {
  "use memo";

  const $ = _c(70);
  const {
    viewer,
    isEditor
  } = useViewer();
  const {
    editUrl
  } = usePageProps();
  const router = useRouter();
  const t = useTranslations("layout.header.user_navbar");
  const user = viewer?.user;
  const analytics = viewer?.user?.allow.analytics;
  const moderateComments = viewer?.user?.allow.moderateComments;
  const commentNotificationCount = viewer?.user?.commentNotificationCount;
  const newsFeedCount = viewer?.user?.newsFeedCount;
  const shouldShowAdminDivider = isEditor && editUrl;
  if (!user) {
    const t0 = `/sign_in?return_to=${router.asPath}`;
    let t1;
    if ($[0] !== t) {
      t1 = t("sign_in");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t0 || $[3] !== t1) {
      t2 = <div className="hidden h-full lg:flex"><Button href={t0}>{t1}</Button></div>;
      $[2] = t0;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    const t3 = `/sign_in?return_to=${router.asPath}`;
    let t4;
    if ($[5] !== t) {
      t4 = t("sign_in");
      $[5] = t;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <CircleUserRound className="w-6" />;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== t3 || $[9] !== t4) {
      t6 = <div className="flex h-full lg:hidden"><Button href={t3} type="bold" ariaLabel={t4}>{t5}</Button></div>;
      $[8] = t3;
      $[9] = t4;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== t2 || $[12] !== t6) {
      t7 = <>{t2}{t6}</>;
      $[11] = t2;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  const {
    avatar,
    name,
    id
  } = user;
  let t0;
  if ($[14] !== analytics || $[15] !== t) {
    t0 = analytics && <Button href={ANALYTICS_URL} role="menuitem">{t("analytics")}</Button>;
    $[14] = analytics;
    $[15] = t;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  let t1;
  if ($[17] !== commentNotificationCount || $[18] !== moderateComments || $[19] !== t) {
    t1 = moderateComments && <Button href="/editorial/comments/editor" role="menuitem">{t("moderate_comments")}{!!commentNotificationCount && <span className="ml-3 inline-block size-4 rounded-full bg-yellow-500" />}</Button>;
    $[17] = commentNotificationCount;
    $[18] = moderateComments;
    $[19] = t;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  let t2;
  if ($[21] !== isEditor || $[22] !== t || $[23] !== user.id) {
    t2 = isEditor && user?.id && <Button role="menuitem" href={`/editorial/dashboards/${user.id}`}>{t("dashboard")}</Button>;
    $[21] = isEditor;
    $[22] = t;
    $[23] = user.id;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  let t3;
  if ($[25] !== t2) {
    t3 = <div className="flex flex-col md:hidden">{t2}</div>;
    $[25] = t2;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  const t4 = `/profiles/${id}/news`;
  let t5;
  if ($[27] !== t) {
    t5 = t("profile");
    $[27] = t;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== t4 || $[30] !== t5) {
    t6 = <Button href={t4} role="menuitem">{t5}</Button>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== t) {
    t7 = t("sign_out");
    $[32] = t;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t7) {
    t8 = <Button href="/sign_out" role="menuitem">{t7}</Button>;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== shouldShowAdminDivider || $[37] !== t) {
    t9 = shouldShowAdminDivider && <><div className="my-1 border-b border-gray-100" role="presentation" /><p className="text-2xs mt-3 mb-1 px-2.5 font-bold">{t("admin")}</p></>;
    $[36] = shouldShowAdminDivider;
    $[37] = t;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== editUrl || $[40] !== isEditor || $[41] !== t) {
    t10 = isEditor && editUrl && <Button href={editUrl} role="menuitem"><PenSquare className="w-6 pr-1" />{t("edit_page")}</Button>;
    $[39] = editUrl;
    $[40] = isEditor;
    $[41] = t;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== t0 || $[44] !== t1 || $[45] !== t10 || $[46] !== t3 || $[47] !== t6 || $[48] !== t8 || $[49] !== t9) {
    t11 = <>{t0}{t1}{t3}{t6}{t8}{t9}{t10}</>;
    $[43] = t0;
    $[44] = t1;
    $[45] = t10;
    $[46] = t3;
    $[47] = t6;
    $[48] = t8;
    $[49] = t9;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  const dropdownOverlay = t11;
  let t12;
  if ($[51] !== t) {
    t12 = t("open_account_menu");
    $[51] = t;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== name) {
    t13 = <div className="mr-1 hidden max-w-30 truncate font-sans text-xs sm:block xl:max-w-40">{name}</div>;
    $[53] = name;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  let t14;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronDown className="mr-1 hidden size-4 md:block" />;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== avatar) {
    t15 = <img src={avatar} alt="" className="my-1 size-7 max-w-none rounded-full lg:size-9" />;
    $[56] = avatar;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== newsFeedCount) {
    t16 = !!newsFeedCount && <div className="text-3xs absolute -top-0.5 -right-2 rounded-full bg-red-500 px-1.5 py-0.5 font-sans font-bold text-white ring-2 ring-white">{newsFeedCount}</div>;
    $[58] = newsFeedCount;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t15 || $[61] !== t16) {
    t17 = <div className="relative -mt-px">{t15}{t16}</div>;
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== t12 || $[64] !== t13 || $[65] !== t17) {
    t18 = <Button ariaLabel={t12} type="icon" ariaExpanded={false}>{t13}{t14}{t17}</Button>;
    $[63] = t12;
    $[64] = t13;
    $[65] = t17;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== dropdownOverlay || $[68] !== t18) {
    t19 = <Dropdown overlay={dropdownOverlay} placement="right" wideOverlay={true}>{t18}</Dropdown>;
    $[67] = dropdownOverlay;
    $[68] = t18;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  return t19;
};